import { memberContactsVerification } from '@pm/super-library-2/modules/member';

import { turnstileSkipLiteApp } from 'root/util/turnstile-util';
import commonUtil from 'root/util/common-util';

import { AD_ELEMENT_EVENT } from '@utils/constants';
import {
    gu_proceedNestedObjectWithVM,
    gu_getNestedValue,
    gu_getSettingFromPortalSettings,
    gu_triggerAdElementAction,
    gu_triggerExternalMerchantAction,
    gu_initComm100,
} from '@utils/general-util';
import { fetchPixelPostbackSettingAction, missionDiaryAction } from '@redux/action';
import { userConstants, USER_LOGIN_ERROR_CODE, CLIENT_EVENT_NAME } from '@redux/constant';
import { setToken } from '@redux/slice/turnstile-slice';
import { updateTurnstileEnabled } from '@redux/slice/auth-settings-slice';

export const userAction = {
    login,
    isAuthenticated,
    logout: logout,
    clearResetPassword,
    hideHomepageAnnouncement,
    hideDepositAnnouncement,
    hideWithdrawAnnouncement,
    getUnreadMsg,
    addCryptoChannel,
    updateUserVerifications,
    updateCertainUser,
    updateMissionClaimCount,
    updateMissionClaimCountByCategory,
    updateIsBalanceMasked,
    getAffiliateOrReferralCode,
    updateAffWallet,
    updateAffCredit,
    updateAuthSettings,
    getMemberMsgType,
    checkWithdrawPin,
    setWithdrawPin,
    setDepositStatus,
    getMemberKYCSumsubInfo,
    falseUpdateUserVerifications,
    updateUserVerificationsPro,
};

function login(loginObj, language, isPendingReviewAcc = false, isAllowTurnstileForLiteApp = false, isReloadPageAfterLogin = false) {
    const username = loginObj.login;
    const { country, key } = language;

    let externalParam = {
        login: username,
        country,
    };
    gu_triggerExternalMerchantAction('login', externalParam);

    const adElementParam = {
        country_code: country,
        lang_code: key.toUpperCase(),
        user_id: username,
    };
    gu_triggerAdElementAction(AD_ELEMENT_EVENT.LOGIN_EVENT, adElementParam);

    return (dispatch) => {
        return new Promise(function (resolve, reject) {
            dispatch({ type: userConstants.LOGIN_REQUEST, payload: loginObj });
            window.SPL_Member.authenticate(loginObj)
                .then((data) => {
                    sessionStorage.removeItem('phoneVerificationDialogPopOut');
                    window.SPL_Cookie.setCookie('EVENT_POSTBACK_PARAMS_ACTION', loginObj.eventPostbackParamAction ?? CLIENT_EVENT_NAME.LOGIN);
                    if (isReloadPageAfterLogin) {
                        resolve(data);
                        return;
                    }
                    dispatch(setToken(''));
                    dispatch({ type: userConstants.LOGIN_SUCCESS, payload: { isLogin: true, isManualLogin: true } });
                    dispatch({ type: userConstants.IS_LOADING, payload: { isLoading: true } });
                    resolve(data);
                })
                .catch((err) => {
                    dispatch(setToken(''));
                    if (err.errCode === USER_LOGIN_ERROR_CODE?.TURNSTILE_ATTEMPT_REACH) {
                        // lite app handling
                        const showTurnstile = turnstileSkipLiteApp(isAllowTurnstileForLiteApp);
                        const payload = {
                            module: window.isAffiliate ? 'AFF_LOGIN' : 'LOGIN',
                            moduleEnableTurnstile: showTurnstile,
                        };
                        // auth settings dispatch login module turnstile enabled
                        dispatch(updateTurnstileEnabled(payload));

                        // dispatch(setTurnstileEnabled(payload));
                    }
                    // errCode 1210 will redirect to register
                    if (err.errCode === 1210) {
                        reject(err);
                    } else if ([1205, 1204].includes(err.errCode) && isPendingReviewAcc) {
                        dispatch({
                            type: userConstants.WAITING_APPROVE_ACCOUNT,
                            payload: { isPendingReviewAcc },
                        });
                    } else {
                        dispatch({ type: userConstants.LOGIN_FAILURE, payload: { isLogin: false, isFailedLogin: true } });
                        dispatch({ type: userConstants.SHOW_RESET_PASSWORD, payload: { showResetPassword: true, message: err.errMsg, code: err.errCode } });
                        reject(err);
                    }
                });
        });
    };
}

function isAuthenticated() {
    return (dispatch, getState) => {
        const currentState = getState();
        const postbackPixel = (account) => {
            const disablePixelPostback = currentState?.portal?.settings?.disablePixelPostback;

            const eventName = window.SPL_Cookie.getCookie('EVENT_POSTBACK_PARAMS_ACTION');
            if (eventName) {
                if (String(eventName).includes(CLIENT_EVENT_NAME.SIGN_UP)) {
                    let externalParam = {
                        username: account.login,
                        affiliate: account.affiliateCode,
                        currency: localStorage.getItem('currencyLang'),
                    };
                    commonUtil.triggerExternalMerchantAction('registerSuccess', externalParam);
                }
            }

            if (!disablePixelPostback) {
                const fetchPixelPostbackSettingPromise = dispatch(
                    fetchPixelPostbackSettingAction({ eventNames: eventName || '', login: account.login, aff_id: account.affiliateName, referralSource: account.referralCode })
                );
                if (eventName) {
                    fetchPixelPostbackSettingPromise
                        .then(() => {
                            window.SPL_Cookie.deleteCookie('EVENT_POSTBACK_PARAMS_ACTION');
                        })
                        .catch(() => {
                            window.SPL_Cookie.deleteCookie('EVENT_POSTBACK_PARAMS_ACTION');
                        });
                }
            } else if (eventName) {
                window.SPL_Cookie.deleteCookie('EVENT_POSTBACK_PARAMS_ACTION');
            }
        };

        return new Promise(function (resolve) {
            if (window.isAffiliate) {
                window.SPL_Affiliate.getAffiliate(
                    {},
                    (account) => {
                        if (account) {
                            dispatch({ type: userConstants.LOGIN_SUCCESS, payload: { isLogin: true } });
                            dispatch({ type: userConstants.IS_LOADING, payload: { isLoading: false } });
                            dispatch({ type: userConstants.GET_MEMBER_ACCOUNT, payload: account });
                            postbackPixel(account);
                        }
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });

                        resolve();
                    },
                    () => {
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });
                        dispatch({ type: userConstants.GET_MEMBER_ACCOUNT_FAILED, payload: null });

                        resolve();
                    }
                );
                window.SPL_Affiliate.getAffiliateSetting().then((res) => {
                    dispatch(affiliateSettings(res));
                });
            } else if (window.isAgent) {
                window.SPL_Agent.getAgent(
                    {},
                    (account) => {
                        if (account) {
                            dispatch({ type: userConstants.LOGIN_SUCCESS, payload: { isLogin: true } });
                            dispatch({ type: userConstants.IS_LOADING, payload: { isLoading: false } });
                            dispatch({ type: userConstants.GET_MEMBER_ACCOUNT, payload: account });
                            postbackPixel(account);
                        }
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });
                        resolve();
                    },
                    () => {
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });
                        dispatch({ type: userConstants.GET_MEMBER_ACCOUNT_FAILED, payload: null });
                        resolve();
                    }
                );
            } else {
                const bioEnabled = window.SPL_LiteApp.isTouchBioAuthUnlocked() || window.SPL_LiteApp.isFaceBioAuthUnlocked();
                window.SPL_Member.getMember(
                    { bioEnabled },
                    (account) => {
                        if (account) {
                            if (account.login) {
                                window.lh_ml = account.login;
                                if (window?.LiveChatWidget && currentState?.portal?.settings?.features?.sidebannerSettings?.livechatSettings?.setName)
                                    window.LiveChatWidget.call('set_customer_name', account.login);
                            }

                            if (account.groupName) {
                                window.lh_mgn = account.groupName;
                            }

                            if (account.membershipLevel) {
                                window.lh_mgt = account.membershipLevel;
                            }

                            if (!account.imagePath) {
                                //apply default profile image path
                                const defaultImgPath =
                                    window.templateLayout === '9'
                                        ? '/public/html/default_whitelabel/shared-image/icons/template8/default-profile.png'
                                        : '/public/html/default_whitelabel/shared-image/icons/nav-profile-img.png';
                                account.imagePath = defaultImgPath;
                            }

                            gu_initComm100(null, true, account);

                            dispatch({ type: userConstants.LOGIN_SUCCESS, payload: { isLogin: true } });
                            dispatch({ type: userConstants.IS_LOADING, payload: { isLoading: false } });
                            dispatch({ type: userConstants.GET_MEMBER_ACCOUNT, payload: account });
                            dispatch({ type: userConstants.USER_TOKEN, payload: window?.SPL_Auth?.getToken() });
                            postbackPixel(account);
                        }
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });
                        // dispatch({ type: userConstants.LOGIN_REQUEST, payload: {loading:true} });

                        resolve();
                    },
                    () => {
                        dispatch({ type: userConstants.GET_MEMBER_REQUEST, payload: true });
                        dispatch({ type: userConstants.GET_MEMBER_ACCOUNT_FAILED, payload: null });
                        // dispatch({ type: userConstants.LOGIN_REQUEST, payload: {loading:true} });

                        resolve();
                    }
                );
            }
        });
    };
}

function logout() {
    return (dispatch) => {
        return new Promise(function (resolve, reject) {
            const isTelegramPlatform = sessionStorage.getItem('in-app-browser') === '1';
            isTelegramPlatform && sessionStorage.removeItem('in-app-browser');
            sessionStorage.removeItem('promoId');
            if (window.isAffiliate) {
                window.SPL_Auth.clearToken();
                dispatch({ type: userConstants.LOGOUT, payload: { isLogin: false, isFailedLogin: false } });
                resolve();
            } else {
                let externalParam = {
                    login: window.lh_ml,
                };
                gu_triggerExternalMerchantAction('logout', externalParam);
                window.SPL_Member.logout()
                    .then(() => {
                        window.lh_ml = '';
                        window.lh_mgn = '';
                        window.lh_mgt = '';
                        dispatch({ type: userConstants.LOGOUT, payload: { isLogin: false, isFailedLogin: false } });
                        dispatch(missionDiaryAction.closeDailyCheckInPopup(false));
                        window.SPL_Cookie.setCookie('EVENT_POSTBACK_PARAMS_ACTION', CLIENT_EVENT_NAME.LOGOUT);
                        resolve();
                    })
                    .catch(() => {
                        dispatch({ type: userConstants.LOGOUT, payload: { isLogin: false, isFailedLogin: false } });
                        reject();
                    });
            }
        });
    };
}

function clearResetPassword() {
    return (dispatch) => {
        dispatch({ type: userConstants.SHOW_RESET_PASSWORD, payload: { showResetPassword: false } });
    };
}

function hideHomepageAnnouncement() {
    return (dispatch) => {
        dispatch({ type: userConstants.HIDE_HOMEPAGE_ANNOUNCEMENT, payload: { showAnnoucement: 'hide' } });
    };
}

function hideDepositAnnouncement() {
    return (dispatch) => {
        dispatch({ type: userConstants.HIDE_DEPOSIT_ANNOUNCEMENT, payload: { depositAnnouncement: 'hide' } });
    };
}

function hideWithdrawAnnouncement() {
    return (dispatch) => {
        dispatch({ type: userConstants.HIDE_WITHDRAW_ANNOUNCEMENT, payload: { withdrawAnnouncement: 'hide' } });
    };
}

function getUnreadMsg(
    unreadMessageCount,
    totalUnreadNotificationCount,
    totalUnreadPaymentCount,
    totalUnreadPromotionCount,
    totalUnreadSystemCount,
    totalUnreadExternalRewardCount
) {
    return (dispatch) => {
        dispatch({
            type: userConstants.GET_UNREAD_MSG,
            payload: {
                unreadMsg: unreadMessageCount,
                unreadNotificationsCount: totalUnreadNotificationCount,
                unreadInboxCount: totalUnreadSystemCount,
                unreadPromotionCount: totalUnreadPromotionCount,
                unreadPaymentCount: totalUnreadPaymentCount,
                unreadExternalRewardCount: totalUnreadExternalRewardCount,
            },
        });
    };
}

function addCryptoChannel(getChannel) {
    return (dispatch) => {
        dispatch({
            type: userConstants.ADD_CRYPTO_CHANNEL,
            payload: { channel: getChannel },
        });
    };
}

function updateCertainUser(account) {
    return (dispatch) => {
        dispatch({ type: userConstants.GET_MEMBER_ACCOUNT, payload: account });
    };
}

function updateMissionClaimCount(claim) {
    return (dispatch) => {
        dispatch({ type: userConstants.UPDATE_CLAIM_COUNT, payload: { missionClaimCount: claim } });
    };
}

function updateMissionClaimCountByCategory(claimCategory) {
    return (dispatch) => {
        dispatch({ type: userConstants.UPDATE_CLAIM_COUNT_BY_CATEGORY, payload: { missionClaimCountByCategory: claimCategory } });
    };
}

function updateUserVerifications(props = false, reCallAPI = false) {
    return (dispatch, getState) => {
        const { user } = getState();
        return new Promise(function (resolve) {
            if (!reCallAPI && gu_getNestedValue(props, 'user.verifications')) {
                resolve(gu_getNestedValue(props, 'user.verifications'));
            } else {
                window.SPL_Member.loadContactsVerification({
                    memberId: user?.account?.id,
                }).then((res) => {
                    if (res) {
                        const userCurrency = gu_getNestedValue(props, 'user.account.currency');
                        const vm = { props: props };
                        const settingsConfiguration = gu_getSettingFromPortalSettings({
                            vm: vm,
                            settingName: 'settingsConfiguration',
                        });
                        const memberVerificationSettings = settingsConfiguration.memberVerificationSettings;

                        let userVerificationStep = 0;
                        let supportedVerificationOptions = res;
                        let userFullyVerified = false;

                        if (memberVerificationSettings) {
                            supportedVerificationOptions = {};

                            supportedVerificationOptions.phoneVerified = res.phoneVerified;

                            supportedVerificationOptions.emailVerified = res.emailVerified;

                            if (
                                gu_getNestedValue(memberVerificationSettings, 'birthdateVerification') &&
                                gu_getNestedValue(memberVerificationSettings, 'birthdateVerification').indexOf(userCurrency) > -1
                            ) {
                                supportedVerificationOptions.birthdayVerified = res.birthdayVerified;
                            }

                            if (
                                gu_getNestedValue(memberVerificationSettings, 'bankVerification') &&
                                gu_getNestedValue(memberVerificationSettings, 'bankVerification').indexOf(userCurrency) > -1
                            ) {
                                supportedVerificationOptions.withdrawalVerified = res.withdrawalVerified;
                            }

                            if (
                                gu_getNestedValue(memberVerificationSettings, 'nameVerification') &&
                                gu_getNestedValue(memberVerificationSettings, 'nameVerification').indexOf(userCurrency) > -1
                            ) {
                                supportedVerificationOptions.nameVerified = res.nameVerified;
                            }
                        }

                        for (let key in supportedVerificationOptions) {
                            if (supportedVerificationOptions[key]) {
                                userVerificationStep++;
                            }
                        }

                        userFullyVerified = userVerificationStep === Object.keys(supportedVerificationOptions).length ? true : false;

                        dispatch({
                            type: userConstants.UPDATE_USER_VERIFICATION,
                            payload: { verifications: { userVerificationStep, userFullyVerified, personal: supportedVerificationOptions, social: {} } },
                        });
                        resolve(res);
                    } else {
                        dispatch({ type: userConstants.UPDATE_USER_VERIFICATION, payload: { verifications: false } });
                        resolve(false);
                    }
                });
            }
        });
    };
}

function updateUserVerificationsPro() {
    // calling spl api and get user verification

    return (dispatch, getState) => {
        const { user, screen, language } = getState();

        const userCurrency = user?.account?.currency || window.currencyLang;
        const settingsConfiguration = window.portalSettings?.features?.settingsConfiguration || {};
        const memberVerificationSettings = gu_proceedNestedObjectWithVM({ props: { screen, language, user } }, settingsConfiguration)?.memberVerificationSettings || {};

        memberContactsVerification({
            memberId: user?.account?.id,
        }).then((res) => {
            if (res) {
                const personal = {};
                personal.emailVerified = res.email;
                personal.phoneVerified = res.phone;
                if (memberVerificationSettings?.birthdateVerification?.includes(userCurrency)) {
                    personal.birthdayVerified = res.birthday;
                }

                if (memberVerificationSettings?.bankVerification?.includes(userCurrency)) {
                    personal.withdrawalVerified = res.withdrawal;
                }

                if (memberVerificationSettings?.nameVerification?.includes(userCurrency)) {
                    personal.nameVerified = res.name;
                }
                dispatch({
                    type: userConstants.UPDATE_USER_VERIFICATION,
                    payload: { verifications: { personal } },
                });
            }
        });
    };
}

function falseUpdateUserVerifications(type) {
    return (dispatch, getState) => {
        const { user } = getState();
        const tempVerification = user?.verifications?.personal;

        switch (type) {
            case 'birthday':
                tempVerification['birthdayVerified'] = true;
                break;
            case 'email':
                tempVerification['emailVerified'] = true;
                break;
            case 'phone':
                tempVerification['phoneVerified'] = true;
                break;
            case 'withdrawal':
                tempVerification['withdrawalVerified'] = true;
                break;
            default:
                return;
        }

        dispatch({
            type: userConstants.UPDATE_USER_VERIFICATION,
            payload: { verifications: { ...user?.verifications, personal: tempVerification } },
        });
    };
}

function updateIsBalanceMasked(isBalanceMasked) {
    return (dispatch) => {
        dispatch({ type: userConstants.UPDATE_IS_BALANCE_MASKED, payload: { isBalanceMasked: isBalanceMasked } });
    };
}

function getAffiliateOrReferralCode(compSettings) {
    return (dispatch) => {
        return new Promise(function (resolve) {
            let refCode = window.SPL_Cookie.getCookie('RefCode');
            let refCurrency = window.SPL_Cookie.getCookie('Ref_Currency');
            let affCode = window.SPL_Cookie.getCookie('AffCode');
            let hideExistedAffiliateIDField =
                compSettings && compSettings.regConfig && compSettings.regConfig.hideExistedAffiliateIDField ? compSettings.regConfig.hideExistedAffiliateIDField : false;
            let hideAffiliateField = hideExistedAffiliateIDField ? hideExistedAffiliateIDField : false;
            let affConfig = {};
            if (refCode) {
                affConfig.affiliateid = refCode;
                affConfig.currency = refCurrency;
                affConfig.disabledAffiliate = true;
                affConfig.hideExistedAffiliateIDField = hideAffiliateField;

                dispatch({ type: userConstants.GET_AFFILIATE_OR_REFERRAL_CODE, payload: { affConfig: affConfig } });
                resolve();
            } else if (affCode) {
                affConfig.affiliateid = affCode;
                affConfig.disabledAffiliate = true;
                affConfig.hideExistedAffiliateIDField = hideAffiliateField;

                dispatch({ type: userConstants.GET_AFFILIATE_OR_REFERRAL_CODE, payload: { affConfig: affConfig } });
                resolve();
            } else {
                window.SPL_Register.getCorrectRegisterAffiliateOrReferral().then((data) => {
                    if (data && data.referralCode) {
                        affConfig.affiliateid = gu_getNestedValue(compSettings, 'regConfig.clearPortalCookie') ? window.SPL_Cookie.deleteCookie('AffCode') : data.referralCode;
                        affConfig.disabledAffiliate = true;
                        affConfig.hideExistedAffiliateIDField = hideAffiliateField;

                        dispatch({ type: userConstants.GET_AFFILIATE_OR_REFERRAL_CODE, payload: { affConfig: affConfig } });
                        resolve();
                    }
                });
            }
        });
    };
}

function updateAffWallet(affWallet) {
    return (dispatch) => {
        dispatch({
            type: userConstants.UPDATE_AFF_WALLET,
            payload: {
                affWallet: affWallet,
            },
        });
    };
}

function updateAffCredit(affCredit) {
    return (dispatch) => {
        dispatch({
            type: userConstants.UPDATE_AFF_CREDIT,
            payload: {
                affCredit: affCredit,
            },
        });
    };
}

function updateAuthSettings(authSettings) {
    return (dispatch) => {
        dispatch({ type: userConstants.UPDATE_AUTH_SETTINGS, payload: { authSettings: authSettings } });
    };
}

function getMemberMsgType(messageType) {
    return (dispatch) => {
        dispatch({
            type: userConstants.GET_MEMBER_MSG_TYPE,
            payload: {
                messageType: messageType,
            },
        });
    };
}
//Old Pin logic pending remove -Start

function checkWithdrawPin() {
    return (dispatch) => {
        return new Promise(function (resolve) {
            window.SPL_Transaction.getCheckWithdrawPin().then((res) => {
                dispatch({
                    type: userConstants.CHECK_WITHDRAW_PIN,
                    payload: {
                        requestWithdrawPin: true,
                        withdrawPinStatus: res,
                    },
                });
                resolve();
            });
        });
    };
}

function setWithdrawPin(showWithdrawPinStatus, otpParams = {}) {
    return (dispatch) => {
        return new Promise(function (resolve) {
            if (Object.keys(otpParams).length > 0) {
                window.SPL_Transaction.setWithdrawalPinOtp(otpParams);
            }
            dispatch({
                type: userConstants.CHECK_WITHDRAW_PIN,
                payload: {
                    withdrawPinStatus: showWithdrawPinStatus,
                },
            });
            resolve();
        });
    };
}
//Old withdraw pin login, pending remove -End

function setDepositStatus(flag) {
    return (dispatch) => {
        dispatch({
            type: userConstants.SET_DEPOSIT_STATUS,
            payload: {
                isDeposited: flag,
            },
        });
    };
}

function affiliateSettings(data) {
    return (dispatch) => {
        dispatch({ type: userConstants.AFFILIATE_SETTINGS, payload: data });
    };
}

function getMemberKYCSumsubInfo(data) {
    return (dispatch) => {
        dispatch({ type: userConstants.MEMBER_SUMSUB_KYC_INFO, payload: data });
    };
}
